import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Modal } from '@bb/ui';
import { ApiError, type AdminApi } from '../../api/client.ts';

const DEFAULT = ['profile_id', 'external_id'];

/**
 * «Export users» (BB-50): choose the columns of a CSV of a segment or the global control group. Personal columns need
 * pii.view and are shown disabled without it. The file is made in the background and listed in My exports.
 */
export function ExportDialog({ environmentId, target, title, open, onOpenChange }: { environmentId: string; target: { kind: 'segment'; segmentId: string } | { kind: 'global_control' }; title: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const columns = useQuery({ queryKey: ['export-columns', environmentId], queryFn: () => api.exportColumns(environmentId), enabled: open });
  const [chosen, setChosen] = useState<string[]>(DEFAULT);
  const request = useMutation({ mutationFn: () => api.requestExport(environmentId, target, chosen) });
  const close = (next: boolean) => {
    if (!next) request.reset();
    onOpenChange(next);
  };
  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={title}
      description="Choose the columns of the CSV. It is made in the background and can be downloaded for 4 hours."
      footer={
        request.isSuccess ? (
          <Button onClick={() => close(false)}>Close</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button onClick={() => request.mutate()} disabled={chosen.length === 0 || request.isPending}>
              Export
            </Button>
          </>
        )
      }
    >
      {request.isSuccess ? (
        <p role="status">
          Export queued.{' '}
          <Link to="/env/$environmentId/settings/exports" params={{ environmentId }}>
            My exports
          </Link>{' '}
          shows it when the file is ready.
          {request.data.droppedColumns.length > 0 && ` Left out without pii.view: ${request.data.droppedColumns.join(', ')}.`}
        </p>
      ) : (
        <fieldset aria-label="Columns">
          {(columns.data?.items ?? []).map((c) => (
            <label key={c.id}>
              <input type="checkbox" disabled={!c.allowed} checked={chosen.includes(c.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))} /> {c.label}
              {c.pii && <span className="hint">{c.allowed ? ' (personal data)' : ' (personal data — needs pii.view)'}</span>}
            </label>
          ))}
        </fieldset>
      )}
      {request.isError && <p role="alert">{request.error instanceof ApiError ? request.error.message : 'The export could not be started.'}</p>}
    </Modal>
  );
}
